import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import api from '../../lib/axios';
import {
  Plus, X, Trash2, GripVertical, Folder, File, Mic, MicOff, Upload, Link as LinkIcon,
  Check, Loader2, Calendar, Hash, Star, Palette, Layout, Package, ChevronDown,
  FileText, ListChecks, Settings, Sparkles, Film, Euro
} from 'lucide-react';
import { cn } from '../../lib/utils';

// Budget Estimate component (Agency only)
const BudgetEstimate = ({ value = {}, onChange, hourlyRate = 90 }) => {
  const [estimate, setEstimate] = useState({
    hours: value.hours || '',
    rate: value.rate || hourlyRate,
    notes: value.notes || ''
  });

  const updateEstimate = (field, val) => {
    const updated = { ...estimate, [field]: val };
    setEstimate(updated);
    
    // Calculate total
    const hours = parseFloat(updated.hours) || 0;
    const rate = parseFloat(updated.rate) || 0;
    const total = hours * rate;
    
    onChange({
      ...updated,
      total_ht: total,
      total_ttc: total * 1.2 // 20% TVA
    });
  };

  const totalHT = (parseFloat(estimate.hours) || 0) * (parseFloat(estimate.rate) || 0);
  const totalTTC = totalHT * 1.2;

  return (
    <div className="space-y-4 p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg border border-amber-200">
      <div className="flex items-center gap-2 text-amber-700 font-medium">
        <Euro className="w-5 h-5" />
        Estimation interne (non visible par le client)
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm text-slate-600">Heures estimées</Label>
          <div className="relative">
            <Input
              type="number"
              value={estimate.hours}
              onChange={(e) => updateEstimate('hours', e.target.value)}
              placeholder="Ex: 40"
              className="pr-8"
              min="0"
              step="0.5"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">h</span>
          </div>
        </div>
        
        <div className="space-y-2">
          <Label className="text-sm text-slate-600">Taux horaire</Label>
          <div className="relative">
            <Input
              type="number"
              value={estimate.rate}
              onChange={(e) => updateEstimate('rate', e.target.value)}
              placeholder="90"
              className="pr-8"
              min="0"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
          </div>
        </div>
      </div>
      
      <div className="space-y-2">
        <Label className="text-sm text-slate-600">Notes internes</Label>
        <Textarea
          value={estimate.notes}
          onChange={(e) => updateEstimate('notes', e.target.value)}
          placeholder="Notes pour l'équipe (complexité, risques, etc.)"
          rows={2}
          className="text-sm"
        />
      </div>
      
      <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
        <div>
          <p className="text-sm text-slate-500">Total estimé HT</p>
          <p className="text-lg font-bold text-slate-900">
            {totalHT.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-slate-500">Total TTC (20%)</p>
          <p className="text-lg font-bold text-emerald-600">
            {totalTTC.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
          </p>
        </div>
      </div>
    </div>
  );
};

// Color picker component
const ColorPicker = ({ value = [], onChange, maxColors = 5, label }) => {
  const [colors, setColors] = useState(value || []);
  const [newColor, setNewColor] = useState('#3B82F6');

  const addColor = () => {
    if (colors.length >= maxColors) {
      toast.error(`Maximum ${maxColors} couleurs`);
      return;
    }
    if (!colors.includes(newColor)) {
      const updated = [...colors, newColor];
      setColors(updated);
      onChange(updated);
    }
  };

  const removeColor = (colorToRemove) => {
    const updated = colors.filter(c => c !== colorToRemove);
    setColors(updated);
    onChange(updated);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {colors.map((color, idx) => (
          <div
            key={idx}
            className="flex items-center gap-1 px-2 py-1 rounded-full border bg-white"
          >
            <div
              className="w-5 h-5 rounded-full border"
              style={{ backgroundColor: color }}
            />
            <span className="text-xs font-mono">{color}</span>
            <button
              type="button"
              onClick={() => removeColor(color)}
              className="text-slate-400 hover:text-red-500"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="color"
          value={newColor}
          onChange={(e) => setNewColor(e.target.value)}
          className="w-10 h-10 rounded cursor-pointer"
        />
        <Input
          value={newColor}
          onChange={(e) => setNewColor(e.target.value)}
          placeholder="#000000"
          className="w-28 font-mono text-sm"
        />
        <Button type="button" variant="outline" size="sm" onClick={addColor}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// Multi-select component with checkboxes
const MultiSelect = ({ options = [], value = [], onChange, label }) => {
  const toggleOption = (optionValue) => {
    const updated = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    onChange(updated);
  };

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        {options.map((option) => (
          <label
            key={option.value}
            className={cn(
              "flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors",
              value.includes(option.value)
                ? "bg-blue-50 border-blue-200"
                : "bg-white hover:bg-slate-50"
            )}
          >
            <Checkbox
              checked={value.includes(option.value)}
              onCheckedChange={() => toggleOption(option.value)}
            />
            <span className="text-sm">{option.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

// URL input with multiple links
const UrlInput = ({ value = [], onChange, placeholder }) => {
  const [links, setLinks] = useState(value.length > 0 ? value : ['']);

  const updateLink = (index, newValue) => {
    const updated = [...links];
    updated[index] = newValue;
    setLinks(updated);
    onChange(updated.filter(l => l.trim()));
  };

  const addLink = () => {
    setLinks([...links, '']);
  };

  const removeLink = (index) => {
    if (links.length === 1) {
      setLinks(['']);
      onChange([]);
    } else {
      const updated = links.filter((_, i) => i !== index);
      setLinks(updated);
      onChange(updated.filter(l => l.trim()));
    }
  };

  return (
    <div className="space-y-2">
      {links.map((link, index) => (
        <div key={index} className="flex gap-2">
          <div className="relative flex-1">
            <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="url"
              value={link}
              onChange={(e) => updateLink(index, e.target.value)}
              placeholder={placeholder || "https://..."}
              className="pl-9"
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => removeLink(index)}
            className="text-red-500 hover:text-red-700"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={addLink}>
        <Plus className="w-4 h-4 mr-1" />
        Ajouter un lien
      </Button>
    </div>
  );
};

// File upload component
const FileUpload = ({ value = [], onChange, label, multiple = true }) => {
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    // In a real app, upload to server and get URLs
    // For now, we'll store file names
    const fileData = files.map(f => ({
      name: f.name,
      size: f.size,
      type: f.type
    }));
    onChange(multiple ? [...value, ...fileData] : fileData);
  };

  const removeFile = (index) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      <div className="border-2 border-dashed rounded-lg p-4 text-center hover:bg-slate-50 transition-colors">
        <input
          type="file"
          multiple={multiple}
          onChange={handleFileChange}
          className="hidden"
          id={`file-upload-${label}`}
        />
        <label htmlFor={`file-upload-${label}`} className="cursor-pointer">
          <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
          <p className="text-sm text-slate-600">Cliquez pour sélectionner des fichiers</p>
          <p className="text-xs text-slate-400">ou glissez-déposez</p>
        </label>
      </div>
      {value.length > 0 && (
        <div className="space-y-1">
          {value.map((file, idx) => (
            <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
              <span className="text-sm truncate">{file.name}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeFile(idx)}
                className="h-6 w-6 p-0 text-red-500"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Scale rating component
const ScaleInput = ({ value = 0, onChange, max = 5 }) => {
  return (
    <div className="flex gap-1">
      {[...Array(max)].map((_, idx) => (
        <button
          key={idx}
          type="button"
          onClick={() => onChange(idx + 1)}
          className={cn(
            "w-8 h-8 rounded transition-colors",
            idx < value
              ? "bg-yellow-400 text-white"
              : "bg-slate-100 text-slate-400 hover:bg-slate-200"
          )}
        >
          <Star className="w-4 h-4 mx-auto" fill={idx < value ? "currentColor" : "none"} />
        </button>
      ))}
    </div>
  );
};

// Sitemap builder component
const SitemapBuilder = ({ value = [], onChange, defaultPages = [] }) => {
  // Use ref to track initialization
  const hasInitialized = useRef(false);
  
  // Initialize state with value or defaultPages directly in useState
  const initialPages = value.length > 0 ? value : defaultPages;
  const [pages, setPages] = useState(initialPages);

  // Only notify parent once when using defaults
  useEffect(() => {
    if (!hasInitialized.current && value.length === 0 && defaultPages.length > 0) {
      hasInitialized.current = true;
      // Notify parent of default values
      onChange(defaultPages);
    }
  }, [value.length, defaultPages, onChange]);

  const addPage = (parentIndex = null) => {
    if (parentIndex !== null) {
      const newPages = [...pages];
      if (!newPages[parentIndex].children) {
        newPages[parentIndex].children = [];
      }
      newPages[parentIndex].children.push({
        name: 'Nouvelle sous-page',
        level: 1
      });
      setPages(newPages);
      onChange(newPages);
    } else {
      const newPages = [...pages, { name: 'Nouvelle page', level: 0, children: [] }];
      setPages(newPages);
      onChange(newPages);
    }
  };

  const updatePage = (index, name, subIndex = null) => {
    const newPages = [...pages];
    if (subIndex !== null) {
      newPages[index].children[subIndex].name = name;
    } else {
      newPages[index].name = name;
    }
    setPages(newPages);
    onChange(newPages);
  };

  const removePage = (index, subIndex = null) => {
    let newPages;
    if (subIndex !== null) {
      newPages = [...pages];
      newPages[index].children.splice(subIndex, 1);
    } else {
      newPages = pages.filter((_, i) => i !== index);
    }
    setPages(newPages);
    onChange(newPages);
  };

  const totalPages = pages.reduce((acc, p) => acc + 1 + (p.children?.length || 0), 0);

  return (
    <div className="space-y-3">
      {pages.map((page, index) => (
        <div key={index} className="space-y-2">
          <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
            <GripVertical className="w-4 h-4 text-slate-400 cursor-grab" />
            <Folder className="w-4 h-4 text-blue-500" />
            <Input
              value={page.name}
              onChange={(e) => updatePage(index, e.target.value)}
              className="flex-1 h-8 text-sm"
              placeholder="Nom de la page"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => addPage(index)}
              className="h-7 px-2 text-blue-600 hover:text-blue-700"
              title="Ajouter une sous-page"
            >
              <Plus className="w-3 h-3" />
            </Button>
            {pages.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removePage(index)}
                className="h-7 px-2 text-red-500 hover:text-red-700"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
          
          {page.children && page.children.length > 0 && (
            <div className="ml-8 space-y-1">
              {page.children.map((subpage, subIndex) => (
                <div key={subIndex} className="flex items-center gap-2 p-2 bg-white border rounded-lg">
                  <div className="w-4" />
                  <File className="w-4 h-4 text-slate-400" />
                  <Input
                    value={subpage.name}
                    onChange={(e) => updatePage(index, e.target.value, subIndex)}
                    className="flex-1 h-8 text-sm"
                    placeholder="Nom de la sous-page"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removePage(index, subIndex)}
                    className="h-7 px-2 text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      
      <Button
        type="button"
        variant="outline"
        onClick={() => addPage()}
        className="w-full mt-2"
      >
        <Plus className="w-4 h-4 mr-2" />
        Ajouter une page
      </Button>
      
      <div className="p-2 bg-blue-50 rounded-lg text-sm text-blue-700">
        <strong>Total :</strong> {totalPages} pages
      </div>
    </div>
  );
};

// Deliverables selector component
const DeliverablesSelector = ({ value = [], onChange, defaultDeliverables = [] }) => {
  const hasInitialized = useRef(false);
  
  // Initialize with value or defaults
  const getInitialDeliverables = () => {
    if (value.length > 0) return value;
    return defaultDeliverables.map(d => ({
      ...d,
      checked: d.default || false
    }));
  };
  
  const [deliverables, setDeliverables] = useState(getInitialDeliverables);
  const [newDeliverable, setNewDeliverable] = useState('');

  // Notify parent of initial selection once
  useEffect(() => {
    if (!hasInitialized.current && value.length === 0 && defaultDeliverables.length > 0) {
      hasInitialized.current = true;
      const initList = defaultDeliverables.map(d => ({
        ...d,
        checked: d.default || false
      }));
      onChange(initList.filter(d => d.checked));
    }
  }, [value.length, defaultDeliverables, onChange]);

  const toggleDeliverable = (id) => {
    const updated = deliverables.map(d =>
      d.id === id ? { ...d, checked: !d.checked } : d
    );
    setDeliverables(updated);
    onChange(updated.filter(d => d.checked));
  };

  const addDeliverable = () => {
    if (!newDeliverable.trim()) return;
    const newItem = {
      id: `custom_${Date.now()}`,
      label: newDeliverable.trim(),
      checked: true
    };
    const updated = [...deliverables, newItem];
    setDeliverables(updated);
    onChange(updated.filter(d => d.checked));
    setNewDeliverable('');
  };

  const removeDeliverable = (id) => {
    const updated = deliverables.filter(d => d.id !== id);
    setDeliverables(updated);
    onChange(updated.filter(d => d.checked));
  };

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {deliverables.map((item) => (
          <div
            key={item.id}
            className={cn(
              "flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer",
              item.checked ? "bg-emerald-50 border-emerald-200" : "bg-white hover:bg-slate-50"
            )}
            onClick={() => toggleDeliverable(item.id)}
          >
            <div className="flex items-center gap-3">
              <div className={cn(
                "w-5 h-5 rounded border-2 flex items-center justify-center transition-colors",
                item.checked ? "bg-emerald-500 border-emerald-500" : "border-slate-300"
              )}>
                {item.checked && <Check className="w-3 h-3 text-white" />}
              </div>
              <span className={cn("text-sm", item.checked ? "text-emerald-800" : "text-slate-700")}>
                {item.label}
              </span>
            </div>
            {item.id.startsWith('custom_') && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); removeDeliverable(item.id); }}
                className="h-7 px-2 text-red-500 hover:text-red-700"
              >
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>
        ))}
      </div>
      
      <div className="flex gap-2">
        <Input
          value={newDeliverable}
          onChange={(e) => setNewDeliverable(e.target.value)}
          placeholder="Ajouter un livrable personnalisé..."
          className="flex-1"
          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDeliverable())}
        />
        <Button
          type="button"
          variant="outline"
          onClick={addDeliverable}
          disabled={!newDeliverable.trim()}
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      
      <div className="p-2 bg-emerald-50 rounded-lg text-sm text-emerald-700">
        <strong>{deliverables.filter(d => d.checked).length}</strong> livrable(s) sélectionné(s)
      </div>
    </div>
  );
};

// Client Elements Selector Component - Éléments attendus du client
const ClientElementsSelector = ({ value = [], onChange, defaultElements = [] }) => {
  const hasInitialized = useRef(false);
  
  // Initialize with value or defaults
  const getInitialElements = () => {
    if (value.length > 0) return value;
    return defaultElements.map(e => ({
      ...e,
      id: e.id || `elem_${Date.now()}_${Math.random()}`,
      checked: e.default || false,
      received: false
    }));
  };
  
  const [elements, setElements] = useState(getInitialElements);
  const [newElement, setNewElement] = useState('');

  // Notify parent of initial selection once
  useEffect(() => {
    if (!hasInitialized.current && value.length === 0 && defaultElements.length > 0) {
      hasInitialized.current = true;
      const initList = defaultElements.map(e => ({
        ...e,
        id: e.id || `elem_${Date.now()}_${Math.random()}`,
        checked: e.default || false,
        received: false
      }));
      onChange(initList.filter(e => e.checked));
    }
  }, [value.length, defaultElements, onChange]);

  const toggleElement = (id) => {
    const updated = elements.map(e =>
      e.id === id ? { ...e, checked: !e.checked } : e
    );
    setElements(updated);
    onChange(updated.filter(e => e.checked));
  };

  const addElement = () => {
    if (!newElement.trim()) return;
    const newItem = {
      id: `custom_elem_${Date.now()}`,
      label: newElement.trim(),
      checked: true,
      received: false
    };
    const updated = [...elements, newItem];
    setElements(updated);
    onChange(updated.filter(e => e.checked));
    setNewElement('');
  };

  const removeElement = (id) => {
    const updated = elements.filter(e => e.id !== id);
    setElements(updated);
    onChange(updated.filter(e => e.checked));
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">
        Éléments que le client doit fournir pour démarrer le projet
      </p>
      <div className="space-y-2">
        {elements.map((item) => (
          <div
            key={item.id}
            className={cn(
              "flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer",
              item.checked ? "bg-amber-50 border-amber-200" : "bg-white hover:bg-slate-50"
            )}
            onClick={() => toggleElement(item.id)}
          >
            <div className="flex items-center gap-3">
              <div className={cn(
                "w-5 h-5 rounded border-2 flex items-center justify-center transition-colors",
                item.checked ? "bg-amber-500 border-amber-500" : "border-slate-300"
              )}>
                {item.checked && <Check className="w-3 h-3 text-white" />}
              </div>
              <span className={cn("text-sm", item.checked ? "text-amber-800" : "text-slate-700")}>
                {item.label}
              </span>
            </div>
            {item.id.startsWith('custom_elem_') && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); removeElement(item.id); }}
                className="h-7 px-2 text-red-500 hover:text-red-700"
              >
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>
        ))}
      </div>
      
      <div className="flex gap-2">
        <Input
          value={newElement}
          onChange={(e) => setNewElement(e.target.value)}
          placeholder="Ajouter un élément attendu..."
          className="flex-1"
          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addElement())}
        />
        <Button
          type="button"
          variant="outline"
          onClick={addElement}
          disabled={!newElement.trim()}
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      
      <div className="p-2 bg-amber-50 rounded-lg text-sm text-amber-700">
        <strong>{elements.filter(e => e.checked).length}</strong> élément(s) attendu(s) du client
      </div>
    </div>
  );
};

// Main Dynamic Brief Form Component
const DynamicBriefForm = ({ 
  projectType, 
  values = {}, 
  onChange, 
  isAgency = true,
  onVoiceInput
}) => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState(values);
  const [expandedSections, setExpandedSections] = useState({});

  // Fetch template when project type changes
  useEffect(() => {
    if (!projectType) {
      setTemplate(null);
      return;
    }

    const fetchTemplate = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/brief-templates/type/${projectType}`);
        setTemplate(response.data);
        
        // Initialize form values with defaults
        const initialValues = { ...formValues };
        response.data.fields?.forEach(field => {
          if (initialValues[field.id] === undefined && field.default_value !== undefined) {
            initialValues[field.id] = field.default_value;
          }
        });
        setFormValues(initialValues);
        
        // Expand all sections by default
        const sections = [...new Set(response.data.fields?.map(f => f.section) || [])];
        const expanded = {};
        sections.forEach(s => expanded[s] = true);
        setExpandedSections(expanded);
      } catch (error) {
        console.error('Error fetching template:', error);
        // Fallback - no template available
        setTemplate(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectType]);

  // Update parent when form values change
  useEffect(() => {
    onChange(formValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues]);

  const updateField = (fieldId, value) => {
    setFormValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Render a single field based on its type
  const renderField = (field) => {
    // Skip agency-only fields if not agency
    if (field.agency_only && !isAgency) {
      return null;
    }

    const value = formValues[field.id];
    const commonProps = {
      id: field.id,
      placeholder: field.placeholder
    };

    switch (field.field_type) {
      case 'text':
        return (
          <div className="relative">
            <Input
              {...commonProps}
              value={value || ''}
              onChange={(e) => updateField(field.id, e.target.value)}
            />
            {field.with_voice_input && onVoiceInput && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                onClick={() => onVoiceInput(field.id)}
              >
                <Mic className="w-4 h-4" />
              </Button>
            )}
          </div>
        );

      case 'textarea':
        return (
          <div className="relative">
            <Textarea
              {...commonProps}
              value={value || ''}
              onChange={(e) => updateField(field.id, e.target.value)}
              rows={4}
            />
            {field.with_voice_input && onVoiceInput && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-2 top-2 h-7 w-7 p-0"
                onClick={() => onVoiceInput(field.id)}
              >
                <Mic className="w-4 h-4" />
              </Button>
            )}
          </div>
        );

      case 'select':
        return (
          <Select
            value={value || ''}
            onValueChange={(v) => updateField(field.id, v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Sélectionner..." />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'multiselect':
        return (
          <MultiSelect
            options={field.options || []}
            value={value || []}
            onChange={(v) => updateField(field.id, v)}
          />
        );

      case 'number':
        return (
          <Input
            {...commonProps}
            type="number"
            value={value || ''}
            onChange={(e) => updateField(field.id, parseFloat(e.target.value) || '')}
            min={field.min_value}
            max={field.max_value}
          />
        );

      case 'date':
        return (
          <Input
            {...commonProps}
            type="date"
            value={value || ''}
            onChange={(e) => updateField(field.id, e.target.value)}
          />
        );

      case 'url':
        return (
          <UrlInput
            value={value || []}
            onChange={(v) => updateField(field.id, v)}
            placeholder={field.placeholder}
          />
        );

      case 'file':
        return (
          <FileUpload
            value={value || []}
            onChange={(v) => updateField(field.id, v)}
            label={field.id}
          />
        );

      case 'color':
        return (
          <ColorPicker
            value={value || []}
            onChange={(v) => updateField(field.id, v)}
            maxColors={field.max_value || 5}
          />
        );

      case 'scale':
        return (
          <ScaleInput
            value={value || 0}
            onChange={(v) => updateField(field.id, v)}
            max={field.max_value || 5}
          />
        );

      case 'sitemap':
        return (
          <SitemapBuilder
            value={value || []}
            onChange={(v) => updateField(field.id, v)}
            defaultPages={[
              { name: 'Accueil', level: 0, children: [] },
              { name: 'À propos', level: 0, children: [] },
              { name: 'Services', level: 0, children: [] },
              { name: 'Contact', level: 0, children: [] }
            ]}
          />
        );

      case 'deliverables':
        return (
          <DeliverablesSelector
            value={value || []}
            onChange={(v) => updateField(field.id, v)}
            defaultDeliverables={template?.deliverables || []}
            label="Livrables de l'agence"
            description="Ces éléments seront livrés au client et créeront des tâches"
          />
        );

      case 'client_elements':
        return (
          <ClientElementsSelector
            value={value || []}
            onChange={(v) => updateField(field.id, v)}
            defaultElements={field.options || []}
          />
        );

      case 'budget_estimate':
        return (
          <BudgetEstimate
            value={value || {}}
            onChange={(v) => updateField(field.id, v)}
            hourlyRate={90}
          />
        );

      default:
        return (
          <Input
            {...commonProps}
            value={value || ''}
            onChange={(e) => updateField(field.id, e.target.value)}
          />
        );
    }
  };

  // Group fields by section
  const getFieldsBySection = () => {
    if (!template?.fields) return {};
    
    const grouped = {};
    template.fields
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .forEach(field => {
        const section = field.section || 'general';
        if (!grouped[section]) {
          grouped[section] = [];
        }
        grouped[section].push(field);
      });
    return grouped;
  };

  const SECTION_LABELS = {
    general: 'Informations générales',
    specifications: 'Spécifications',
    technical: 'Technique',
    structure: 'Structure',
    inspiration: 'Inspiration',
    colors: 'Couleurs',
    style: 'Style',
    features: 'Fonctionnalités',
    audio: 'Audio',
    shooting: 'Tournage',
    post: 'Post-production',
    catalog: 'Catalogue',
    payment: 'Paiement',
    shipping: 'Livraison',
    campaign: 'Campagne',
    targeting: 'Ciblage',
    objectives: 'Objectifs',
    assets: 'Assets',
    design: 'Design',
    deliverables: 'Livrables agence',
    client_elements: 'Éléments attendus du client',
    budget: 'Estimation Budget (Agence)'
  };

  const SECTION_ICONS = {
    general: FileText,
    specifications: ListChecks,
    technical: Settings,
    structure: Layout,
    inspiration: Sparkles,
    colors: Palette,
    style: Palette,
    features: Package,
    audio: Mic,
    shooting: Film,
    post: Film,
    deliverables: Package,
    client_elements: Upload,
    budget: Euro
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="text-center py-8 text-slate-500">
        <p>Aucun template disponible pour ce type de projet.</p>
        <p className="text-sm">Utilisez le formulaire générique.</p>
      </div>
    );
  }

  const fieldsBySection = getFieldsBySection();

  return (
    <div className="space-y-4">
      {Object.entries(fieldsBySection).map(([section, fields]) => {
        const SectionIcon = SECTION_ICONS[section] || FileText;
        const visibleFields = fields.filter(f => !f.agency_only || isAgency);
        
        if (visibleFields.length === 0) return null;

        return (
          <Card key={section} className="border-0 shadow-sm">
            <CardHeader
              className="cursor-pointer hover:bg-slate-50 transition-colors"
              onClick={() => toggleSection(section)}
            >
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <SectionIcon className="w-5 h-5 text-blue-600" />
                  {SECTION_LABELS[section] || section}
                </CardTitle>
                <ChevronDown
                  className={cn(
                    "w-5 h-5 text-slate-400 transition-transform",
                    expandedSections[section] && "rotate-180"
                  )}
                />
              </div>
            </CardHeader>
            
            {expandedSections[section] && (
              <CardContent className="space-y-4">
                {visibleFields.map((field) => (
                  <div key={field.id} className="space-y-2">
                    <Label className="flex items-center gap-2">
                      {field.label}
                      {field.required && <span className="text-red-500">*</span>}
                      {field.agency_only && (
                        <Badge variant="outline" className="text-xs text-orange-600">
                          Agence
                        </Badge>
                      )}
                    </Label>
                    {field.description && (
                      <p className="text-xs text-slate-500">{field.description}</p>
                    )}
                    {renderField(field)}
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default DynamicBriefForm;
export { ColorPicker, MultiSelect, UrlInput, FileUpload, ScaleInput, SitemapBuilder, DeliverablesSelector };
